import React, { useState, useRef, useEffect } from 'react';
import { Globe, Shield, ShieldCheck, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import ShareLinkDisplay from '../components/ShareLinkDisplay';
import UploadProgress from '../components/UploadProgress';
import CardboardBox3D from '../components/CardboardBox3D';
import SendOptionsModal from '../components/SendOptionsModal';
import P2PTransferModal from '../components/P2PTransferModal';
import NavigationMenu from '../components/NavigationMenu';
import DynamicFooter from '../components/DynamicFooter';
import { useAuth } from '../contexts/AuthContext';
import { generateKey, exportKey, encryptFile, isEncryptionSupported } from '../utils/encryption';
import JSZip from 'jszip';
import { downloadZip, predictLength } from 'client-zip';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { token, user, isUltimate } = useAuth();
  const isPro = user?.is_pro || user?.is_admin || false;
  const [p2pMode, setP2pMode] = useState(false);
  const [showP2PModal, setShowP2PModal] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [boxHovered, setBoxHovered] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [shareData, setShareData] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);
  const [encryptionKey, setEncryptionKey] = useState(null);
  const [themeImage, setThemeImage] = useState(null);
  const [multipartThresholdMB, setMultipartThresholdMB] = useState(5);

  useEffect(() => {
    axios.get(`${API}/themes/active`).then(res => {
      if (res.data.has_theme && res.data.theme?.image_url) {
        const url = res.data.theme.image_url;
        setThemeImage(url.startsWith('/api/') ? `${BACKEND_URL}${url}` : url);
      }
    }).catch(() => {});
    // Load configurable multipart threshold (admin can adjust in backoffice)
    axios.get(`${API}/public/plan-limits`).then(res => {
      if (typeof res.data?.multipart_threshold_mb === 'number') {
        setMultipartThresholdMB(res.data.multipart_threshold_mb);
      }
    }).catch(() => {});
  }, []);

  // Warn the user if they try to leave the page during an active upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading) {
        e.preventDefault();
        e.returnValue = 'Un upload est en cours. Quitter interrompra le transfert.';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploading]);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const cancelRef = useRef(false);
  const { toast } = useToast();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      // Ajouter les fichiers droppés aux fichiers existants
      setSelectedFiles(prevFiles => {
        const existingNames = new Set(prevFiles.map(f => f.name));
        const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));
        return [...prevFiles, ...uniqueNewFiles];
      });
      toast({
        title: 'Fichiers ajoutés',
        description: `${newFiles.length} fichier(s) ajouté(s)`,
      });
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles(prevFiles => prevFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      // Ajouter les nouveaux fichiers aux fichiers existants (accumulation)
      setSelectedFiles(prevFiles => {
        const existingNames = new Set(prevFiles.map(f => f.name));
        const uniqueNewFiles = newFiles.filter(f => !existingNames.has(f.name));
        const allFiles = [...prevFiles, ...uniqueNewFiles];
        return allFiles;
      });
      toast({
        title: 'Fichiers ajoutés',
        description: `${newFiles.length} fichier(s) ajouté(s)`,
      });
    }
    // Reset input pour permettre de re-sélectionner le même fichier
    e.target.value = '';
  };

  const uploadMultipartStream = async (stream, filename, totalSize, mimeType, emailData = null, password = null) => {
    // Upload a ReadableStream as a multipart upload (no need to have the whole file in memory)
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    let initRes;
    try {
      initRes = await axios.post(`${API}/upload/multipart/initiate`, {
        filename,
        file_size: totalSize,
        mime_type: mimeType || 'application/octet-stream',
        email_data: emailData,
      }, { headers });
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('non disponible')) throw new Error('MULTIPART_UNAVAILABLE');
      throw err;
    }

    const { upload_id, part_size } = initRes.data;
    const PART = Math.max(part_size || 0, 10 * 1024 * 1024);
    const totalParts = Math.ceil(totalSize / PART);
    let bytesUploaded = 0;
    const uploadedParts = [];

    // Get presigned URLs for all parts
    let presignedUrls = {};
    let useDirectUpload = false;
    try {
      const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);
      const presignRes = await axios.post(`${API}/upload/multipart/presign`, {
        upload_id, part_numbers: partNumbers,
      }, { headers });
      presignedUrls = presignRes.data.urls || {};
      useDirectUpload = Object.keys(presignedUrls).length === totalParts;
      console.log(`[SENDBOX] Stream upload: ${useDirectUpload ? 'DIRECT to Wasabi' : 'Backend relay'} | Chunks: ${totalParts} x ${(PART/1024/1024).toFixed(0)}MB | Total: ${(totalSize/1024/1024/1024).toFixed(2)} GB`);
    } catch (err) {
      console.warn('[SENDBOX] Presigned URLs unavailable, using backend relay', err);
    }

    const uploadPart = async (partNumber, body) => {
      const MAX_RETRIES = 5;
      let lastError = null;
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        if (cancelRef.current) throw new Error('cancelled');
        try {
          let etag;
          if (useDirectUpload && presignedUrls[partNumber]) {
            const res = await fetch(presignedUrls[partNumber], { method: 'PUT', body });
            if (!res.ok) throw new Error(`Wasabi PUT failed: ${res.status}`);
            etag = res.headers.get('ETag') || res.headers.get('etag');
            if (!etag) throw new Error('ETag missing');
          } else {
            const form = new FormData();
            form.append('upload_id', upload_id);
            form.append('part_number', partNumber);
            form.append('chunk', body);
            const res = await axios.post(`${API}/upload/multipart/part`, form, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' },
              timeout: 600000,
            });
            etag = res.data.etag;
          }
          return etag;
        } catch (err) {
          lastError = err;
          if (useDirectUpload && attempt === 0 && err.message?.includes('Failed to fetch')) {
            useDirectUpload = false;
          }
          if (attempt < MAX_RETRIES - 1) {
            await new Promise((r) => setTimeout(r, Math.min(1000 * Math.pow(2, attempt), 10000)));
          }
        }
      }
      throw new Error(`Chunk ${partNumber} failed: ${lastError?.message}`);
    };

    try {
      // Read the stream and accumulate to PART size, then upload
      const reader = stream.getReader();
      let buffer = new Uint8Array(0);
      let partNumber = 1;

      while (true) {
        if (cancelRef.current) {
          await axios.post(`${API}/upload/multipart/abort`, new URLSearchParams({ upload_id }).toString(), { headers: { ...headers, 'Content-Type': 'application/x-www-form-urlencoded' } });
          setUploading(false);
          toast({ title: 'Transfert annulé' });
          return;
        }
        const { value, done } = await reader.read();
        if (done) break;

        // Append to buffer
        const newBuffer = new Uint8Array(buffer.length + value.length);
        newBuffer.set(buffer);
        newBuffer.set(value, buffer.length);
        buffer = newBuffer;

        // Upload full parts
        while (buffer.length >= PART) {
          const partBytes = buffer.slice(0, PART);
          buffer = buffer.slice(PART);
          const etag = await uploadPart(partNumber, new Blob([partBytes]));
          uploadedParts.push({ part_number: partNumber, etag });
          bytesUploaded += partBytes.length;
          setUploadedBytes(bytesUploaded);
          setUploadProgress(Math.min(95, (bytesUploaded / totalSize) * 95));
          partNumber++;
        }
      }

      // Upload final part (whatever remains)
      if (buffer.length > 0) {
        const etag = await uploadPart(partNumber, new Blob([buffer]));
        uploadedParts.push({ part_number: partNumber, etag });
        bytesUploaded += buffer.length;
        setUploadedBytes(bytesUploaded);
        setUploadProgress(95);
      }

      setUploadProgress(98);
      const completeRes = await axios.post(`${API}/upload/multipart/complete`, {
        upload_id, parts: uploadedParts, password,
        is_encrypted: false,
        scheduled_at: emailData?.scheduledAt || null,
      }, { headers });

      setUploadProgress(100);
      const data = { ...completeRes.data };
      if (emailData) {
        data.recipient_email = emailData.recipientEmail;
        data.sender_email = emailData.senderEmail;
      }
      setShareData(data);
      setSelectedFiles([]);
      setUploading(false);

      if (emailData) {
        toast({ title: 'Envoi réussi !', description: `Un email a été envoyé à ${emailData.recipientEmail}` });
      }
    } catch (error) {
      try {
        await axios.post(`${API}/upload/multipart/abort`, new URLSearchParams({ upload_id }).toString(), { headers: { ...headers, 'Content-Type': 'application/x-www-form-urlencoded' } });
      } catch {}
      if (cancelRef.current) return;
      console.error('Stream upload error:', error);
      setUploading(false);
      toast({
        title: "Échec de l'envoi",
        description: error.response?.data?.detail || error.message || "Impossible d'envoyer",
        variant: 'destructive',
      });
    }
  };

  const uploadMultipart = async (file, emailData = null, password = null) => {
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    let initRes;
    try {
      initRes = await axios.post(`${API}/upload/multipart/initiate`, {
        filename: file.name,
        file_size: file.size,
        mime_type: file.type || 'application/octet-stream',
        email_data: emailData,
      }, { headers });
    } catch (err) {
      // If multipart isn't available (no Wasabi), throw to let caller fallback
      const detail = err.response?.data?.detail || '';
      if (detail.includes('non disponible')) {
        throw new Error('MULTIPART_UNAVAILABLE');
      }
      throw err;
    }

    const { upload_id, part_size } = initRes.data;
    // Use larger chunks (10 MB) to reduce overhead per request
    const PART = Math.max(part_size || 0, 10 * 1024 * 1024);
    const totalParts = Math.ceil(file.size / PART);
    const totalSize = file.size;
    let bytesUploaded = 0;
    const uploadedParts = [];
    const MAX_PARALLEL = 6;
    const MAX_RETRIES = 5;

    // Try to get presigned URLs for direct frontend → Wasabi upload (much faster, bypasses backend)
    let presignedUrls = {};
    let useDirectUpload = false;
    try {
      const partNumbers = Array.from({ length: totalParts }, (_, i) => i + 1);
      const presignRes = await axios.post(`${API}/upload/multipart/presign`, {
        upload_id,
        part_numbers: partNumbers,
      }, { headers });
      presignedUrls = presignRes.data.urls || {};
      useDirectUpload = Object.keys(presignedUrls).length === totalParts;
      console.log(`[SENDBOX] Upload mode: ${useDirectUpload ? 'DIRECT to Wasabi (fast)' : 'Backend relay (slow)'} | Chunks: ${totalParts} x ${(PART/1024/1024).toFixed(0)}MB | Parallel: ${MAX_PARALLEL}`);
    } catch (err) {
      console.warn('[SENDBOX] Presigned URLs unavailable, falling back to backend relay', err);
    }

    const uploadOnePart = async (partNumber) => {
      const offset = (partNumber - 1) * PART;
      const blob = file.slice(offset, offset + PART);

      let lastError = null;
      for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
        if (cancelRef.current) throw new Error('cancelled');
        try {
          let etag;
          if (useDirectUpload && presignedUrls[partNumber]) {
            // Direct PUT to Wasabi via presigned URL (bypasses backend, way faster)
            const res = await fetch(presignedUrls[partNumber], {
              method: 'PUT',
              body: blob,
            });
            if (!res.ok) throw new Error(`Wasabi PUT failed: ${res.status}`);
            etag = res.headers.get('ETag') || res.headers.get('etag');
            if (!etag) throw new Error('ETag missing in Wasabi response');
          } else {
            // Fallback: relay through backend
            const form = new FormData();
            form.append('upload_id', upload_id);
            form.append('part_number', partNumber);
            form.append('chunk', blob);
            const res = await axios.post(`${API}/upload/multipart/part`, form, {
              headers: { ...headers, 'Content-Type': 'multipart/form-data' },
              timeout: 300000,
            });
            etag = res.data.etag;
          }
          uploadedParts.push({ part_number: partNumber, etag });
          bytesUploaded += blob.size;
          setUploadedBytes(bytesUploaded);
          setUploadProgress(Math.min(95, (bytesUploaded / totalSize) * 95));
          return;
        } catch (err) {
          lastError = err;
          // If direct upload fails on first attempt with CORS error, fallback to backend
          if (useDirectUpload && attempt === 0 && err.message && err.message.includes('Failed to fetch')) {
            console.warn('Direct upload blocked (CORS?), switching to backend relay');
            useDirectUpload = false;
          }
          if (attempt < MAX_RETRIES - 1) {
            const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
            await new Promise((r) => setTimeout(r, delay));
          }
        }
      }
      throw new Error(`Chunk ${partNumber} failed after ${MAX_RETRIES} retries: ${lastError?.message}`);
    };

    try {
      let queue = [];
      for (let n = 1; n <= totalParts; n++) {
        if (cancelRef.current) {
          await axios.post(`${API}/upload/multipart/abort`, new URLSearchParams({ upload_id }).toString(), { headers: { ...headers, 'Content-Type': 'application/x-www-form-urlencoded' } });
          setUploading(false);
          setUploadProgress(0);
          setUploadedBytes(0);
          toast({ title: 'Transfert annulé', description: "L'envoi a été annulé" });
          return;
        }
        queue.push(uploadOnePart(n));
        if (queue.length >= MAX_PARALLEL) {
          await Promise.all(queue);
          queue = [];
        }
      }
      if (queue.length > 0) await Promise.all(queue);

      setUploadProgress(98);
      const completeRes = await axios.post(`${API}/upload/multipart/complete`, {
        upload_id,
        parts: uploadedParts,
        password,
        is_encrypted: false,
        scheduled_at: emailData?.scheduledAt || null,
      }, { headers });

      setUploadProgress(100);
      const data = { ...completeRes.data };
      if (emailData) {
        data.recipient_email = emailData.recipientEmail;
        data.sender_email = emailData.senderEmail;
      }
      setShareData(data);
      setSelectedFiles([]);
      setUploading(false);

      if (emailData) {
        toast({ title: 'Envoi réussi !', description: `Un email a été envoyé à ${emailData.recipientEmail}` });
      }
    } catch (error) {
      // Abort multipart upload on Wasabi to avoid orphan parts
      try {
        await axios.post(`${API}/upload/multipart/abort`, new URLSearchParams({ upload_id }).toString(), { headers: { ...headers, 'Content-Type': 'application/x-www-form-urlencoded' } });
      } catch {}
      if (cancelRef.current) return;
      console.error('Multipart upload error:', error);
      setUploading(false);
      toast({
        title: "Échec de l'envoi",
        description: error.response?.data?.detail || error.message || "Impossible d'envoyer le fichier",
        variant: 'destructive',
      });
    }
  };

  const uploadFiles = async (files, emailData = null, password = null) => {
    try {
      setUploading(true);
      setUploadProgress(0);
      setUploadedBytes(0);
      setShowSendModal(false);
      cancelRef.current = false;

      // Multipart threshold is configurable in Admin → Configuration
      const totalBytes = files.reduce((acc, f) => acc + f.size, 0);
      const thresholdBytes = multipartThresholdMB * 1024 * 1024;
      const useMultipart = !encryptionEnabled && (files.length > 1 || totalBytes >= thresholdBytes);
      if (useMultipart) {
        try {
          if (files.length === 1) {
            // Single file: stream directly via multipart
            return await uploadMultipart(files[0], emailData, password);
          } else {
            // Multi-file: STREAMING ZIP via client-zip (low RAM, supports very large totals)
            toast({ title: 'Préparation...', description: 'Création de l\'archive en streaming' });

            // Dedupe filenames inside the zip
            const usedNames = new Set();
            const entries = files.map((f) => {
              let name = f.name;
              if (usedNames.has(name)) {
                const dot = name.lastIndexOf('.');
                const base = dot > 0 ? name.slice(0, dot) : name;
                const ext = dot > 0 ? name.slice(dot) : '';
                let i = 2;
                while (usedNames.has(`${base} (${i})${ext}`)) i++;
                name = `${base} (${i})${ext}`;
              }
              usedNames.add(name);
              return { name, lastModified: new Date(f.lastModified || Date.now()), input: f };
            });

            // Predict exact ZIP size (client-zip calculates it deterministically)
            const zipSize = Number(predictLength(entries));
            const zipResponse = downloadZip(entries);
            const zipStream = zipResponse.body;

            return await uploadMultipartStream(
              zipStream,
              'sendbox_archive.zip',
              zipSize,
              'application/zip',
              emailData,
              password,
            );
          }
        } catch (err) {
          if (err.message !== 'MULTIPART_UNAVAILABLE') throw err;
          console.warn('Falling back to classic upload (multipart unavailable)');
        }
      }

      // E2E Encryption: encrypt files before upload
      let filesToUpload = files;
      let keyString = null;
      let originalFilenames = files.map(f => f.name);
      if (encryptionEnabled && isEncryptionSupported()) {
        const key = await generateKey();
        keyString = await exportKey(key);
        setEncryptionKey(keyString);
        
        toast({ title: 'Chiffrement en cours...', description: 'Vos fichiers sont en cours de chiffrement' });
        
        let fileToEncrypt;
        if (files.length > 1) {
          // Multi-file: ZIP first, then encrypt the ZIP
          const zip = new JSZip();
          for (const file of files) {
            zip.file(file.name, file);
          }
          const zipBlob = await zip.generateAsync({ type: 'blob' });
          fileToEncrypt = new File([zipBlob], 'archive.zip', { type: 'application/zip' });
        } else {
          fileToEncrypt = files[0];
        }
        
        const encryptedBlob = await encryptFile(fileToEncrypt, key);
        const encryptedFile = new File([encryptedBlob], fileToEncrypt.name, { type: 'application/octet-stream' });
        filesToUpload = [encryptedFile];
      }

      const filesInfo = filesToUpload.map((f, i) => ({
        filename: encryptionEnabled && files.length > 1 ? 'archive.zip' : files[0]?.name || f.name,
        file_size: f.size,
        mime_type: encryptionEnabled ? 'application/octet-stream' : (f.type || 'application/octet-stream')
      }));

      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const initiateRes = await axios.post(`${API}/upload/initiate`, {
        files: filesInfo,
        email_data: emailData
      }, { headers });

      const { upload_id, chunk_size } = initiateRes.data;
      const CHUNK = Math.min(chunk_size, 2 * 1024 * 1024); // 2 Mo max pour connexions lentes

      let totalSize = filesToUpload.reduce((acc, f) => acc + f.size, 0);
      let bytesUploaded = 0;
      const MAX_PARALLEL = 3;

      for (let fileIndex = 0; fileIndex < filesToUpload.length; fileIndex++) {
        const file = filesToUpload[fileIndex];
        const totalChunks = Math.ceil(file.size / CHUNK);
        let chunkQueue = [];

        for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
          if (cancelRef.current) {
            setUploading(false);
            setUploadProgress(0);
            setUploadedBytes(0);
            toast({ title: 'Transfert annulé', description: 'L\'envoi a été annulé' });
            return;
          }

          const offset = chunkIndex * CHUNK;
          const chunkBlob = file.slice(offset, offset + CHUNK);

          const uploadPromise = (async () => {
            const formData = new FormData();
            formData.append('upload_id', upload_id);
            formData.append('file_index', fileIndex);
            formData.append('chunk_index', chunkIndex);
            formData.append('chunk', chunkBlob);

            // Retry logic: up to 5 attempts with exponential backoff
            const MAX_RETRIES = 5;
            let lastError = null;
            for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
              try {
                await axios.post(`${API}/upload/chunk-binary`, formData, {
                  headers: { ...headers, 'Content-Type': 'multipart/form-data' },
                  timeout: 120000, // 2 min per chunk
                });
                lastError = null;
                break;
              } catch (err) {
                lastError = err;
                if (attempt < MAX_RETRIES - 1) {
                  const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
                  await new Promise((resolve) => setTimeout(resolve, delay));
                }
              }
            }
            if (lastError) {
              throw new Error(`Échec de l'upload du chunk ${chunkIndex} après ${MAX_RETRIES} tentatives: ${lastError.message}`);
            }
            bytesUploaded += chunkBlob.size;
            setUploadedBytes(bytesUploaded);
            setUploadProgress(Math.min(90, (bytesUploaded / totalSize) * 90));
          })();

          chunkQueue.push(uploadPromise);

          if (chunkQueue.length >= MAX_PARALLEL) {
            await Promise.all(chunkQueue);
            chunkQueue = [];
          }
        }
        if (chunkQueue.length > 0) {
          await Promise.all(chunkQueue);
        }
      }

      setUploadProgress(95);
      setUploadedBytes(totalSize);
      const completeRes = await axios.post(`${API}/upload/complete`, {
        upload_id,
        password: password,
        is_encrypted: encryptionEnabled,
        scheduled_at: emailData?.scheduledAt || null,
        original_filenames: encryptionEnabled && files.length > 1 ? originalFilenames : undefined,
        encryption_key_for_email: (encryptionEnabled && emailData) ? keyString : undefined
      }, { headers });
      
      setUploadProgress(100);
      
      // Add encryption key to share data (for URL fragment)
      const data = { ...completeRes.data };
      if (keyString && encryptionEnabled) {
        data.encryption_key = keyString;
      }
      if (emailData) {
        data.recipient_email = emailData.recipientEmail;
        data.sender_email = emailData.senderEmail;
      }
      setShareData(data);
      setSelectedFiles([]);
      setUploading(false);

      if (emailData) {
        if (data.email_scheduled) {
          toast({
            title: 'Envoi programmé !',
            description: `L'email sera envoyé à ${emailData.recipientEmail} à la date choisie`,
          });
        } else {
          toast({
            title: 'Envoi réussi !',
            description: `Un email a été envoyé à ${emailData.recipientEmail}`,
          });
        }
      }

    } catch (error) {
      if (cancelRef.current) return;
      console.error('Upload error:', error);
      setUploading(false);
      toast({
        title: 'Échec de l\'envoi',
        description: error.response?.data?.detail || 'Impossible d\'envoyer les fichiers',
        variant: 'destructive',
      });
    }
  };

  const handleCancelUpload = () => {
    cancelRef.current = true;
  };

  const handleSendLink = (password = null) => {
    uploadFiles(selectedFiles, null, password);
  };

  const handleSendEmail = (emailData, password = null) => {
    uploadFiles(selectedFiles, emailData, password);
  };

  const handleCreateShareLink = () => {
    if (selectedFiles.length === 0) {
      toast({
        title: 'Aucun fichier selectionne',
        description: 'Veuillez selectionner au moins un fichier a partager',
        variant: 'destructive',
      });
      return;
    }
    if (p2pMode) {
      setShowP2PModal(true);
      return;
    }
    setShowSendModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-slate-900 to-blue-900 text-white relative overflow-hidden">
      <Toaster />
      
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl animate-pulse" style={{opacity: 0, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite, fadeIn 0.5s ease-out 0.1s forwards'}}></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" style={{opacity: 0, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite 1s, fadeIn 0.5s ease-out 0.2s forwards'}}></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl animate-pulse" style={{opacity: 0, animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite 2s, fadeIn 0.5s ease-out 0.3s forwards'}}></div>
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      </div>
      
      {/* Upload Progress Modal */}
      {uploading && (
        <UploadProgress 
          progress={uploadProgress} 
          filename={selectedFiles[0]?.name}
          totalSize={selectedFiles.reduce((acc, f) => acc + f.size, 0)}
          uploadedBytes={uploadedBytes}
          fileCount={selectedFiles.length}
          onCancel={handleCancelUpload}
        />
      )}

      {/* Share Link Display Modal */}
      {shareData && (
        <ShareLinkDisplay 
          shareData={shareData} 
          onClose={() => setShareData(null)}
          isPro={isPro}
        />
      )}

      {/* Send Options Modal */}
      {showSendModal && (
        <SendOptionsModal
          files={selectedFiles}
          onClose={() => setShowSendModal(false)}
          onSendLink={handleSendLink}
          onSendEmail={handleSendEmail}
          uploading={uploading}
          isPro={isPro}
          userEmail={user?.email || ''}
        />
      )}

      {/* P2P Transfer Modal */}
      {showP2PModal && (
        <P2PTransferModal
          files={selectedFiles}
          token={token}
          onClose={() => {
            setShowP2PModal(false);
            setSelectedFiles([]);
            setP2pMode(false);
          }}
        />
      )}
      
      {/* Navigation Menu - Administrable */}
      <NavigationMenu />

      {/* Main Content */}
      <main className="pt-24 pb-20 px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12 space-y-4">
            <h1 className="text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-400 bg-clip-text text-transparent">
                Simple, Privé
              </span>
              <br />
              <span className="text-white">Partage de Fichiers</span>
            </h1>
            
            <p className="text-lg text-blue-200/80 max-w-2xl mx-auto leading-relaxed">
              Partagez des fichiers jusqu'à <span className="text-cyan-400 font-semibold">20 Go gratuitement</span> avec chiffrement.
              <br />Les liens expirent automatiquement. Vos données restent privées.
            </p>
          </div>

          {/* Interactive Box Upload Area */}
          <div className="max-w-2xl mx-auto">
            <div 
              className={`relative cursor-pointer transition-all duration-500 ${dragActive ? 'scale-105' : 'hover:scale-102'}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-drop-zone"
            >
              {/* Large Interactive Cardboard Box */}
              <div className="flex flex-col items-center justify-center py-8">
                {/* Cardboard Box 3D with animated flaps */}
                <div 
                  onMouseEnter={() => setBoxHovered(true)}
                  onMouseLeave={() => setBoxHovered(false)}
                >
                  <CardboardBox3D 
                    isOpen={selectedFiles.length > 0}
                    isDragging={dragActive}
                    className="w-72 h-64 md:w-80 md:h-72"
                    imageSrc={themeImage}
                  />
                </div>
                
                {/* Text below box */}
                <div className="text-center -mt-2">
                  <h3 className={`text-xl font-semibold transition-all duration-300 ${dragActive ? 'text-cyan-400 opacity-100' : boxHovered ? 'text-white opacity-100' : 'text-white opacity-0'}`}>
                    {dragActive ? 'Déposez vos fichiers ici !' : 'Déposez vos fichiers dans la boîte'}
                  </h3>
                  
                  <div className="flex gap-3 justify-center mt-4">
                    <Button
                      onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
                      className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold px-6 py-5 rounded-xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300"
                      data-testid="select-files-btn"
                    >
                      Sélectionner des fichiers
                    </Button>
                  </div>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-input"
                />
                <input
                  ref={folderInputRef}
                  type="file"
                  webkitdirectory="true"
                  directory="true"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <DynamicFooter fixed={true} />

      {/* Selected Files Display */}
      {selectedFiles.length > 0 && !uploading && (
        <div className="fixed bottom-24 right-6 bg-blue-950/90 backdrop-blur-xl p-6 rounded-2xl border border-blue-500/30 shadow-2xl shadow-blue-500/20 max-w-sm animate-in slide-in-from-bottom-5 z-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-cyan-400">Fichiers sélectionnés ({selectedFiles.length})</h3>
            <span className="text-xs text-blue-300/60">
              {(() => {
                const total = selectedFiles.reduce((acc, f) => acc + f.size, 0);
                if (total < 1024) return `${total} o`;
                if (total < 1024*1024) return `${(total/1024).toFixed(0)} Ko`;
                if (total < 1024*1024*1024) return `${(total/(1024*1024)).toFixed(1)} Mo`;
                return `${(total/(1024*1024*1024)).toFixed(2)} Go`;
              })()}
            </span>
          </div>
          <div className="flex items-center justify-end mb-2">
            <button 
              onClick={() => setSelectedFiles([])}
              className="text-xs text-blue-300/60 hover:text-red-400 transition-colors"
            >
              Tout effacer
            </button>
          </div>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {selectedFiles.map((file, index) => (
              <div key={index} className="text-sm text-blue-200/70 truncate flex items-center justify-between gap-2 group">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-1.5 h-1.5 bg-cyan-400 rounded-full flex-shrink-0"></div>
                  <span className="truncate">{file.name}</span>
                </div>
                <button
                  onClick={() => removeFile(index)}
                  className="text-blue-300/40 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all flex-shrink-0"
                  title="Supprimer"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          {/* E2E Encryption Toggle */}
          {isEncryptionSupported() && (
            <button
              onClick={() => setEncryptionEnabled(!encryptionEnabled)}
              className={`flex items-center gap-2 w-full mt-3 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${encryptionEnabled ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-300' : 'bg-slate-800/50 border-blue-500/10 text-blue-300/50 hover:text-blue-300 hover:border-blue-500/30'}`}
              data-testid="encryption-toggle"
            >
              {encryptionEnabled ? <ShieldCheck className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
              Chiffrement E2E {encryptionEnabled ? 'actif' : 'inactif'}
            </button>
          )}
          {/* P2P Toggle - Ultimate only */}
          {isUltimate && (
            <button
              onClick={() => {
                setP2pMode(!p2pMode);
                if (!p2pMode) setEncryptionEnabled(false);
              }}
              className={`flex items-center gap-2 w-full mt-2 px-3 py-2 rounded-lg text-xs font-medium transition-all border ${p2pMode ? 'bg-purple-500/15 border-purple-500/40 text-purple-300' : 'bg-slate-800/50 border-blue-500/10 text-blue-300/50 hover:text-blue-300 hover:border-blue-500/30'}`}
              data-testid="p2p-toggle"
            >
              <Zap className="w-4 h-4" />
              P2P direct {p2pMode ? 'actif' : 'inactif'}
            </button>
          )}
          <div className="flex gap-2 mt-3">
            <Button
              variant="outline"
              className="flex-1 border-blue-500/30 text-blue-200 hover:bg-blue-500/20"
              onClick={() => fileInputRef.current?.click()}
            >
              + Ajouter
            </Button>
            <Button
              className={`flex-1 font-semibold transition-all duration-300 shadow-lg ${p2pMode ? 'bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 shadow-purple-500/30 text-white' : 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-blue-500/30 text-white'}`}
              onClick={handleCreateShareLink}
              disabled={uploading}
              data-testid="create-share-link-btn"
            >
              {uploading ? 'Envoi...' : p2pMode ? 'Envoyer en P2P' : 'Envoyer'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
